/**
 * Cross-Location Knowledge Transfer Engine
 * Detects improvements at one location and packages them as transferable playbooks.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookCopy, ArrowRight, CheckCircle2, MapPin, TrendingUp, Clock, ChevronDown, Users, Zap, Target } from 'lucide-react';

const PLAYBOOKS = [
  {
    id: 1,
    title: 'Suggestive Sell Script',
    origin: { location: 'Southport', state: 'QLD', date: '3 weeks ago' },
    improvement: { metric: 'Combo Attachment Rate', before: '41%', after: '52%', change: '+11%' },
    revenueImpact: '+$3,800/month at Southport',
    transferTo: [
      { location: 'Footscray', currentValue: '38%', projectedGain: '+$2,900/mo', readiness: 85 },
      { location: 'Prospect', currentValue: '35%', projectedGain: '+$2,100/mo', readiness: 72 },
      { location: 'Joondalup', currentValue: '44%', projectedGain: '+$1,600/mo', readiness: 90 },
    ],
    steps: [
      'Download the 1-page suggestive sell script (PDF)',
      'Run a 30-min training session with shift managers',
      'Add combo prompt card to each register station',
      'Track daily attachment rate on the SPLH dashboard',
      'Review after 2 weeks — adjust script based on top-performing items',
    ],
    effort: '2 hrs setup + 30 min training per location',
    networkImpact: '+$6,600/mo if adopted at all 3 locations',
    status: 'ready',
  },
  {
    id: 2,
    title: 'Pre-Staging DT Workflow',
    origin: { location: 'Southport', state: 'QLD', date: '6 weeks ago' },
    improvement: { metric: 'DT Avg Wait Time', before: '3.4 min', after: '2.8 min', change: '-0.6 min' },
    revenueImpact: '+18 cars/hr throughput at Southport',
    transferTo: [
      { location: 'Penrith', currentValue: '3.2 min', projectedGain: '-0.4 min', readiness: 80 },
      { location: 'Footscray', currentValue: '4.8 min', projectedGain: '-1.0 min', readiness: 60 },
    ],
    steps: [
      'Assign a dedicated DT expeditor during 11am-1pm peak',
      'Install a pre-staging shelf between kitchen and DT window',
      'Train staff to pre-build top-5 most-ordered combos during peak prep',
      'Set a visible timer at the DT window (target: 2.5 min)',
      'Daily debrief on peak-hour throughput numbers',
    ],
    effort: '$200 shelf + 1 hr training per location',
    networkImpact: '-0.7 min avg wait across DT locations',
    status: 'ready',
  },
  {
    id: 3,
    title: 'Shift Overlap Optimisation',
    origin: { location: 'Penrith', state: 'NSW', date: '2 weeks ago' },
    improvement: { metric: 'SPLH', before: '$63.20', after: '$68.50', change: '+$5.30' },
    revenueImpact: 'Labour cost reduced from 26.1% to 24.5%',
    transferTo: [
      { location: 'Prospect', currentValue: '$55.40', projectedGain: '+$6.20', readiness: 75 },
      { location: 'Footscray', currentValue: '$52.10', projectedGain: '+$7.80', readiness: 65 },
    ],
    steps: [
      'Pull 90-day hourly POS revenue report',
      'Map current roster to revenue by day-part',
      'Identify and reduce overlap during low-rev windows (typically 2-4pm)',
      'Redeploy saved hours to peak windows (11am-1pm, 5:30-7:30pm)',
      'Monitor SPLH weekly for 4 weeks',
    ],
    effort: '3 hrs analysis + roster adjustment per location',
    networkImpact: '+$7.00 avg SPLH across underperforming locations',
    status: 'ready',
  },
];

export const KnowledgeTransferEngine = ({ industry = 'qsr' }) => {
  const [expandedId, setExpandedId] = useState(null);

  if (industry !== 'qsr') {
    return (
      <Card className="glass-card" data-testid="knowledge-transfer-engine">
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <BookCopy className="w-5 h-5 text-[#0066CC]" />
            Knowledge Transfer
            <Badge variant="outline" className="text-[10px]">Multi-Location</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-center">
            <BookCopy className="w-8 h-8 text-[#007AFF] mx-auto mb-2" />
            <p className="text-sm font-medium text-teal-800">Knowledge Transfer activates with 2+ teams or locations</p>
            <p className="text-xs text-[#0055B5] mt-1">When Corvana detects an improvement in one team or location, it automatically packages the playbook to roll out across the rest of your business.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card overflow-hidden" data-testid="knowledge-transfer-engine">
      <div className="h-1 bg-gradient-to-r from-[#0066CC] to-[#007AFF]" />
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <BookCopy className="w-5 h-5 text-[#0066CC]" />
            Knowledge Transfer
          </CardTitle>
          <Badge className="bg-[#0066CC]/10 text-[#004A99] border-teal-200 text-[10px]">
            {PLAYBOOKS.length} playbooks ready
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">Best practices detected at top locations, ready to deploy network-wide</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {PLAYBOOKS.map(pb => {
          const isExpanded = expandedId === pb.id;
          return (
            <div
              key={pb.id}
              className="border border-blue-100 rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-sm"
              onClick={() => setExpandedId(isExpanded ? null : pb.id)}
              data-testid={`playbook-${pb.id}`}
            >
              <div className="p-3 bg-gradient-to-r from-blue-50/50 to-cyan-50/50">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800">{pb.title}</span>
                      <Badge variant="outline" className="text-[10px] border-teal-200 text-[#0055B5]">
                        <MapPin className="w-3 h-3 mr-0.5" />
                        {pb.origin.location}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-slate-500">{pb.improvement.metric}:</span>
                      <span className="text-xs text-slate-400">{pb.improvement.before}</span>
                      <ArrowRight className="w-3 h-3 text-[#0066CC]" />
                      <span className="text-xs font-semibold text-[#004A99]">{pb.improvement.after}</span>
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
                        {pb.improvement.change}
                      </Badge>
                    </div>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {isExpanded && (
                <div className="px-3 pb-3 space-y-3 border-t border-blue-100" data-testid={`playbook-detail-${pb.id}`}>
                  {/* Transfer targets */}
                  <div className="pt-3">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Ready to Deploy At</p>
                    <div className="space-y-2">
                      {pb.transferTo.map((target, i) => (
                        <div key={i} className="flex items-center gap-3 bg-white/60 rounded-lg p-2 border border-slate-100">
                          <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="text-xs font-medium w-20">{target.location}</span>
                          <span className="text-[10px] text-slate-500">Current: {target.currentValue}</span>
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 text-[10px]">
                            <TrendingUp className="w-3 h-3 mr-0.5" />{target.projectedGain}
                          </Badge>
                          <div className="flex items-center gap-1 ml-auto">
                            <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full bg-[#0066CC] rounded-full" style={{ width: `${target.readiness}%` }} />
                            </div>
                            <span className="text-[10px] text-slate-500">{target.readiness}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Steps */}
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Implementation Steps</p>
                    <ol className="space-y-1.5">
                      {pb.steps.map((step, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                          <span className="w-4 h-4 rounded-full bg-blue-100 text-[#004A99] text-[10px] flex items-center justify-center shrink-0 mt-0.5 font-semibold">{i + 1}</span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Summary */}
 <div className="grid grid-cols-1 gap-2">
                    <div className="bg-slate-50 rounded-lg p-2 border border-slate-100">
                      <p className="text-[10px] text-slate-400 font-semibold">Effort</p>
                      <p className="text-xs font-medium text-slate-700">{pb.effort}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-2 border border-emerald-100">
                      <p className="text-[10px] text-emerald-500 font-semibold">Network Impact</p>
                      <p className="text-xs font-semibold text-emerald-700">{pb.networkImpact}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default KnowledgeTransferEngine;
